import type { BlogPost } from '../../types/blog';

export const post: BlogPost = {
  category: 'estrategia',
  author: 'Ivan Henrique',
  publishedAt: '2026-04-10',
  coverImage: '/blog/placar-exato-vs-vencedor.svg',
  tags: ['Estratégia', 'Placar exato', 'Vencedor', 'Pontuação'],
  i18n: {
    'pt-BR': {
      slug: 'placar-exato-vs-vencedor',
      title: 'Placar exato ou apenas vencedor: qual estratégia rende mais pontos no bolão?',
      excerpt:
        'Tentar adivinhar o placar perfeito ou apostar no básico (quem ganha)? A matemática do bolão revela qual estratégia rende mais ao longo de uma temporada inteira.',
      readTimeMinutes: 6,
      coverImageAlt: 'Capa: Placar exato ou apenas vencedor?',
      keywords: ['placar exato bolão', 'estratégia palpite', 'pontuação bolão', 'qual estratégia melhor'],
      content: (
        <>
          <p>
            Tem dois tipos de palpiteiro no bolão. O ousado, que arrisca o
            placar exato em quase todo jogo — 3x2, 2x1, placares específicos —
            com a esperança de acertar e levar o bonde. E o cauteloso, que
            vai sempre nos placares mais &quot;estatisticamente comuns&quot; (1x0,
            2x1, 1x1) e foca em acertar quem ganha. Qual estratégia rende
            mais?
          </p>
          <p>
            A resposta tem dados. Vamos analisar.
          </p>

          <h2>As regras de pontuação do Bolão Elite</h2>
          <p>
            O Bolão Elite oferece <strong>3 regras de pontuação</strong>, que
            o criador do bolão escolhe na hora de criar:
          </p>
          <ul>
            <li><strong>Regra A (Simples):</strong> placar exato = 1 ponto. Errou = 0.</li>
            <li><strong>Regra B (Intermediária):</strong> placar exato = 3 pontos, vencedor/empate correto = 1 ponto.</li>
            <li><strong>Regra C (Avançada):</strong> placar exato = 10 pontos, vencedor/empate = 5 pontos, número de gols de um dos times correto = 2 pontos.</li>
          </ul>
          <p>
            Para a análise abaixo, vamos usar a <strong>Regra C</strong>,
            que é a mais granular e melhor pra ilustrar o trade-off entre
            ousar e ser conservador.
          </p>

          <h2>A taxa de acerto real</h2>
          <p>
            Estudos com palpiteiros amadores em bolões de Brasileirão e
            Premier League mostram que:
          </p>
          <ul>
            <li>Taxa média de acerto de <strong>placar exato</strong>: 8% a 12%</li>
            <li>Taxa média de acerto de <strong>gols de um dos times</strong>: 15% a 20%</li>
            <li>Taxa média de acerto de <strong>vencedor</strong>: 50% a 60%</li>
          </ul>
          <p>
            Esses números explicam quase tudo. Você acerta placar exato em
            cerca de 1 em cada 10 jogos. E acerta o vencedor em mais da
            metade dos jogos.
          </p>

          <h2>A matemática mostra o que rende mais</h2>
          <p>
            Vamos fazer a conta usando a <strong>Regra C (10/5/2)</strong>.
            Pega 100 jogos de uma temporada. Calculamos os pontos esperados
            pra cada estratégia:
          </p>

          <h3>Estratégia 1: foco em placar exato</h3>
          <p>
            Você arrisca placar específico em todos os 100 jogos. Acerto
            esperado:
          </p>
          <ul>
            <li>10 placares exatos × 10 pts = 100 pontos</li>
            <li>30 vencedores adicionais × 5 pts = 150 pontos</li>
            <li>15 acertos parciais (gols de um time) × 2 pts = 30 pontos</li>
            <li>Total: <strong>280 pontos</strong></li>
          </ul>

          <h3>Estratégia 2: foco em vencedor</h3>
          <p>
            Você foca apenas em acertar quem ganha, escolhe placares mais
            prováveis (1x0, 2x1). Acerto esperado:
          </p>
          <ul>
            <li>8 placares exatos × 10 pts = 80 pontos</li>
            <li>55 vencedores corretos × 5 pts = 275 pontos</li>
            <li>20 acertos parciais (gols de um time) × 2 pts = 40 pontos</li>
            <li>Total: <strong>395 pontos</strong></li>
          </ul>
          <p>
            <strong>A estratégia conservadora rende 41% mais pontos</strong> ao
            longo de 100 jogos. Não é dramático — é matemática básica.
          </p>

          <h2>Por que a maioria insiste no placar exato</h2>
          <p>
            Por dois motivos psicológicos clássicos:
          </p>
          <ol>
            <li>
              <strong>Acertar placar exato gera euforia.</strong> Aquele
              &quot;acertei 3x2!&quot; vale uma noite inteira de conversa. Acertar
              apenas o vencedor é entediante. Os palpiteiros se motivam mais
              pela emoção do placar exato e esquecem que isso é raro.
            </li>
            <li>
              <strong>Bias de seleção.</strong> A gente lembra muito mais dos
              placares exatos que acertou (10 ao longo do ano) que dos 90 que
              errou. Subestima a frequência do erro.
            </li>
          </ol>

          <h2>Quando o placar exato vale o risco</h2>
          <p>
            Tem 3 situações onde arriscar placar específico faz sentido:
          </p>
          <ol>
            <li>
              <strong>Quando você está no fim da temporada e atrás no ranking.</strong>{' '}
              Se você precisa de muitos pontos rápido pra alcançar o
              líder, arriscar placares específicos é estratégia razoável.
              Você precisa de variância — e variância vem de palpites
              arriscados.
            </li>
            <li>
              <strong>Quando o jogo tem padrão claro (favorito x lanterna).</strong>{' '}
              Em jogos onde a diferença de força é gigante, placares como
              3x0, 4x0, 4x1 acontecem com frequência. O placar exato é
              menos arriscado nesses casos.
            </li>
            <li>
              <strong>Quando você palpita por &quot;sensação&quot; muito
              específica.</strong> Às vezes uma sensação muito forte sobre
              uma partida (um time vai dominar, outro tá morto) merece um
              palpite específico. Mas use isso com parcimônia — é exceção,
              não regra.
            </li>
          </ol>

          <h2>A regra prática</h2>
          <p>
            <strong>Para 80% dos jogos, vá no placar mais provável</strong>{' '}
            (geralmente 1x0, 2x1 ou 1x1 dependendo do equilíbrio das
            equipes). Foque em acertar o vencedor. Tente placar específico
            só nos 20% dos jogos onde você tem leitura especial.
          </p>
          <p>
            No final da temporada, a consistência vence o risco.
          </p>

          <h2>Conclusão</h2>
          <p>
            Bolão é maratona. Quem palpita pra acertar quem ganha leva a
            temporada inteira sem grandes baixos. Quem aposta tudo no placar
            exato pode ter rodadas brilhantes e depois cair pro fundo do
            ranking. A matemática é clara: prudência rende mais.
          </p>
          <p>
            Quer mais dicas?{' '}
            <a href="/blog">Veja todos os artigos do blog</a> ou comece pelo{' '}
            <a href="/como-jogar">guia de como jogar</a>.
          </p>
        </>
      ),
    },
    en: {
      slug: 'exact-score-vs-winner',
      title: 'Exact score or just the winner: which strategy scores more points in your pool?',
      excerpt:
        'Try to guess the perfect score or play it basic (who wins)? The maths of the pool reveals which strategy pays off more across a whole season.',
      readTimeMinutes: 6,
      coverImageAlt: 'Cover: Exact score or just the winner?',
      coverImage: '/blog/placar-exato-vs-vencedor-en.svg',
      keywords: ['exact score pool', 'prediction strategy', 'pool scoring', 'which strategy is best'],
      content: (
        <>
          <p>
            There are two kinds of predictor in a pool. The bold one, who
            risks the exact score on almost every match — 3-2, 2-1, specific
            scores — hoping to nail it and clean up. And the cautious one, who
            always goes with the most &quot;statistically common&quot; scores
            (1-0, 2-1, 1-1) and focuses on picking who wins. Which strategy
            pays off more?
          </p>
          <p>
            The answer has data behind it. Let's break it down.
          </p>

          <h2>Bolão Elite's scoring rules</h2>
          <p>
            Bolão Elite offers <strong>3 scoring rules</strong>, which the
            pool creator chooses when setting it up:
          </p>
          <ul>
            <li><strong>Rule A (Simple):</strong> exact score = 1 point. Wrong = 0.</li>
            <li><strong>Rule B (Intermediate):</strong> exact score = 3 points, correct winner/draw = 1 point.</li>
            <li><strong>Rule C (Advanced):</strong> exact score = 10 points, winner/draw = 5 points, correct number of goals for one of the teams = 2 points.</li>
          </ul>
          <p>
            For the analysis below, we'll use <strong>Rule C</strong>, which
            is the most granular and best for illustrating the trade-off
            between being bold and being conservative.
          </p>

          <h2>The real hit rate</h2>
          <p>
            Studies of amateur predictors in Brazilian league (Brasileirão)
            and Premier League pools show that:
          </p>
          <ul>
            <li>Average hit rate for the <strong>exact score</strong>: 8% to 12%</li>
            <li>Average hit rate for <strong>one team's goals</strong>: 15% to 20%</li>
            <li>Average hit rate for the <strong>winner</strong>: 50% to 60%</li>
          </ul>
          <p>
            These numbers explain almost everything. You hit the exact score
            on roughly 1 in every 10 matches. And you pick the winner on more
            than half of all matches.
          </p>

          <h2>The maths shows what pays off more</h2>
          <p>
            Let's do the maths using <strong>Rule C (10/5/2)</strong>. Take
            100 matches in a season. We work out the expected points for each
            strategy:
          </p>

          <h3>Strategy 1: focus on the exact score</h3>
          <p>
            You risk a specific score on all 100 matches. Expected returns:
          </p>
          <ul>
            <li>10 exact scores × 10 pts = 100 points</li>
            <li>30 additional winners × 5 pts = 150 points</li>
            <li>15 partial matches (one team's goals) × 2 pts = 30 points</li>
            <li>Total: <strong>280 points</strong></li>
          </ul>

          <h3>Strategy 2: focus on the winner</h3>
          <p>
            You focus only on picking who wins, choosing the most likely
            scores (1-0, 2-1). Expected returns:
          </p>
          <ul>
            <li>8 exact scores × 10 pts = 80 points</li>
            <li>55 correct winners × 5 pts = 275 points</li>
            <li>20 partial matches (one team's goals) × 2 pts = 40 points</li>
            <li>Total: <strong>395 points</strong></li>
          </ul>
          <p>
            <strong>The conservative strategy scores 41% more points</strong>{' '}
            across 100 matches. It's not dramatic — it's basic maths.
          </p>

          <h2>Why most people stick with the exact score</h2>
          <p>
            For two classic psychological reasons:
          </p>
          <ol>
            <li>
              <strong>Hitting the exact score creates euphoria.</strong> That
              &quot;I called 3-2!&quot; is worth a whole night of bragging.
              Picking just the winner is boring. Predictors are driven more by
              the thrill of the exact score and forget how rare it is.
            </li>
            <li>
              <strong>Selection bias.</strong> We remember the exact scores we
              got right (10 over the year) far more than the 90 we got wrong.
              We underestimate how often we miss.
            </li>
          </ol>

          <h2>When the exact score is worth the risk</h2>
          <p>
            There are 3 situations where risking a specific score makes sense:
          </p>
          <ol>
            <li>
              <strong>When you're near the end of the season and behind on the leaderboard.</strong>{' '}
              If you need a lot of points fast to catch the leader, risking
              specific scores is a reasonable strategy. You need variance — and
              variance comes from risky predictions.
            </li>
            <li>
              <strong>When the match has a clear pattern (favourite vs bottom team).</strong>{' '}
              In matches where the gap in strength is huge, scores like 3-0,
              4-0, 4-1 happen often. The exact score is less risky in those
              cases.
            </li>
            <li>
              <strong>When you're predicting on a very specific &quot;gut feeling&quot;.</strong>{' '}
              Sometimes a very strong feeling about a match (one team will
              dominate, the other is dead on its feet) deserves a specific
              prediction. But use this sparingly — it's the exception, not the
              rule.
            </li>
          </ol>

          <h2>The practical rule</h2>
          <p>
            <strong>For 80% of matches, go with the most likely score</strong>{' '}
            (usually 1-0, 2-1 or 1-1 depending on how evenly matched the
            teams are). Focus on picking the winner. Try a specific score only
            on the 20% of matches where you have a special read.
          </p>
          <p>
            At the end of the season, consistency beats risk.
          </p>

          <h2>Conclusion</h2>
          <p>
            A pool is a marathon. Whoever predicts to get the winner right
            sails through the whole season without big lows. Whoever bets
            everything on the exact score can have brilliant matchdays and
            then crash to the bottom of the leaderboard. The maths is clear:
            caution pays off more.
          </p>
          <p>
            Want more tips?{' '}
            <a href="/en/blog">See all blog articles</a> or start with the{' '}
            <a href="/en/how-to-play">guide on how to play</a>.
          </p>
        </>
      ),
    },
    es: {
      slug: 'marcador-exacto-vs-ganador',
      title: 'Marcador exacto o solo el ganador: ¿qué estrategia rinde más puntos en la quiniela?',
      excerpt:
        '¿Intentar adivinar el marcador perfecto o jugar a lo básico (quién gana)? Las matemáticas de la quiniela revelan qué estrategia rinde más a lo largo de toda una temporada.',
      readTimeMinutes: 6,
      coverImageAlt: 'Portada: ¿Marcador exacto o solo el ganador?',
      coverImage: '/blog/placar-exato-vs-vencedor-es.svg',
      keywords: ['marcador exacto quiniela', 'estrategia pronóstico', 'puntuación quiniela', 'qué estrategia es mejor'],
      content: (
        <>
          <p>
            Hay dos tipos de pronosticador en la quiniela. El atrevido, que
            arriesga el marcador exacto en casi todos los partidos — 3-2, 2-1,
            marcadores específicos — con la esperanza de acertar y llevárselo
            todo. Y el cauteloso, que va siempre a los marcadores más
            &quot;estadísticamente comunes&quot; (1-0, 2-1, 1-1) y se centra en
            acertar quién gana. ¿Qué estrategia rinde más?
          </p>
          <p>
            La respuesta tiene datos. Vamos a analizarla.
          </p>

          <h2>Las reglas de puntuación de Bolão Elite</h2>
          <p>
            Bolão Elite ofrece <strong>3 reglas de puntuación</strong>, que el
            creador de la quiniela elige al crearla:
          </p>
          <ul>
            <li><strong>Regla A (Simple):</strong> marcador exacto = 1 punto. Fallaste = 0.</li>
            <li><strong>Regla B (Intermedia):</strong> marcador exacto = 3 puntos, ganador/empate correcto = 1 punto.</li>
            <li><strong>Regla C (Avanzada):</strong> marcador exacto = 10 puntos, ganador/empate = 5 puntos, número de goles de uno de los equipos correcto = 2 puntos.</li>
          </ul>
          <p>
            Para el análisis de abajo vamos a usar la <strong>Regla C</strong>,
            que es la más granular y la mejor para ilustrar el equilibrio
            entre arriesgar y ser conservador.
          </p>

          <h2>La tasa de acierto real</h2>
          <p>
            Estudios con pronosticadores aficionados en quinielas del
            Brasileirão y de la Premier League muestran que:
          </p>
          <ul>
            <li>Tasa media de acierto del <strong>marcador exacto</strong>: 8% a 12%</li>
            <li>Tasa media de acierto de <strong>los goles de uno de los equipos</strong>: 15% a 20%</li>
            <li>Tasa media de acierto del <strong>ganador</strong>: 50% a 60%</li>
          </ul>
          <p>
            Estos números lo explican casi todo. Aciertas el marcador exacto
            en aproximadamente 1 de cada 10 partidos. Y aciertas el ganador en
            más de la mitad de los partidos.
          </p>

          <h2>Las matemáticas muestran qué rinde más</h2>
          <p>
            Hagamos la cuenta usando la <strong>Regla C (10/5/2)</strong>. Toma
            100 partidos de una temporada. Calculamos los puntos esperados para
            cada estrategia:
          </p>

          <h3>Estrategia 1: enfoque en el marcador exacto</h3>
          <p>
            Arriesgas un marcador específico en los 100 partidos. Acierto
            esperado:
          </p>
          <ul>
            <li>10 marcadores exactos × 10 pts = 100 puntos</li>
            <li>30 ganadores adicionales × 5 pts = 150 puntos</li>
            <li>15 aciertos parciales (goles de un equipo) × 2 pts = 30 puntos</li>
            <li>Total: <strong>280 puntos</strong></li>
          </ul>

          <h3>Estrategia 2: enfoque en el ganador</h3>
          <p>
            Te centras solo en acertar quién gana, eliges los marcadores más
            probables (1-0, 2-1). Acierto esperado:
          </p>
          <ul>
            <li>8 marcadores exactos × 10 pts = 80 puntos</li>
            <li>55 ganadores correctos × 5 pts = 275 puntos</li>
            <li>20 aciertos parciales (goles de un equipo) × 2 pts = 40 puntos</li>
            <li>Total: <strong>395 puntos</strong></li>
          </ul>
          <p>
            <strong>La estrategia conservadora rinde un 41% más de puntos</strong>{' '}
            a lo largo de 100 partidos. No es dramático — son matemáticas
            básicas.
          </p>

          <h2>Por qué la mayoría insiste en el marcador exacto</h2>
          <p>
            Por dos motivos psicológicos clásicos:
          </p>
          <ol>
            <li>
              <strong>Acertar el marcador exacto genera euforia.</strong> Ese
              &quot;¡acerté 3-2!&quot; vale una noche entera de charla. Acertar
              solo el ganador es aburrido. Los pronosticadores se motivan más
              por la emoción del marcador exacto y olvidan que eso es raro.
            </li>
            <li>
              <strong>Sesgo de selección.</strong> Recordamos mucho más los
              marcadores exactos que acertamos (10 a lo largo del año) que los
              90 que fallamos. Subestimamos la frecuencia del fallo.
            </li>
          </ol>

          <h2>Cuándo el marcador exacto vale el riesgo</h2>
          <p>
            Hay 3 situaciones en las que arriesgar un marcador específico tiene
            sentido:
          </p>
          <ol>
            <li>
              <strong>Cuando estás al final de la temporada y por detrás en la clasificación.</strong>{' '}
              Si necesitas muchos puntos rápido para alcanzar al líder,
              arriesgar marcadores específicos es una estrategia razonable.
              Necesitas varianza — y la varianza viene de pronósticos
              arriesgados.
            </li>
            <li>
              <strong>Cuando el partido tiene un patrón claro (favorito vs colista).</strong>{' '}
              En partidos donde la diferencia de fuerza es enorme, marcadores
              como 3-0, 4-0, 4-1 ocurren con frecuencia. El marcador exacto es
              menos arriesgado en esos casos.
            </li>
            <li>
              <strong>Cuando pronosticas por una &quot;sensación&quot; muy específica.</strong>{' '}
              A veces una sensación muy fuerte sobre un partido (un equipo va a
              dominar, el otro está muerto) merece un pronóstico específico.
              Pero usa esto con moderación — es la excepción, no la regla.
            </li>
          </ol>

          <h2>La regla práctica</h2>
          <p>
            <strong>Para el 80% de los partidos, ve al marcador más probable</strong>{' '}
            (normalmente 1-0, 2-1 o 1-1 según el equilibrio de los equipos).
            Céntrate en acertar el ganador. Intenta un marcador específico solo
            en el 20% de los partidos donde tienes una lectura especial.
          </p>
          <p>
            Al final de la temporada, la consistencia le gana al riesgo.
          </p>

          <h2>Conclusión</h2>
          <p>
            La quiniela es una maratón. Quien pronostica para acertar quién
            gana atraviesa toda la temporada sin grandes bajones. Quien apuesta
            todo al marcador exacto puede tener jornadas brillantes y luego caer
            al fondo de la clasificación. Las matemáticas son claras: la
            prudencia rinde más.
          </p>
          <p>
            ¿Quieres más consejos?{' '}
            <a href="/es/blog">Mira todos los artículos del blog</a> o empieza
            por la{' '}
            <a href="/es/como-jugar">guía de cómo jugar</a>.
          </p>
        </>
      ),
    },
  },
};
